import { Component, OnInit, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, LoadingController, AlertController, IonInput } from '@ionic/angular/lazy';
import { OrderService } from 'src/app/services/order.service';
import { ObserverService } from 'src/app/services/observer.service';

@Component({
    selector: 'app-order-email-invoice',
    templateUrl: './order-email-invoice.page.html',
    styleUrls: ['./order-email-invoice.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class OrderEmailInvoicePage implements OnInit {

    @ViewChild('email_address_input', { static: false }) email_address_input: IonInput;

    spinner = null;
    loading_spinner = null;
    loading = false;
    sending = false;
    online = true;
    input_focused = false;
    @Input() order_id = null;
    order = null;
    @Input() email_address = null;

    constructor(
        public modalController: ModalController,
        public alertController: AlertController,
        public loadingController: LoadingController,
        private orderService: OrderService,
        private observerService: ObserverService
    ) { }

    ngOnInit() {

        if(this.order_id) {


            this.loadData().then(() => { }).catch(() => { });

        }

        this.observerService.subscribe('network_status_changed|order_email_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => { }).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to email an invoice, please check your network connection and try again.");
                }
            }
        });

    }

    // NOTE: focus input after rendering.
    ngAfterViewChecked() {
        if(!this.loading && this.online && !this.input_focused && this.email_address_input) {
            setTimeout(() => {
                this.email_address_input.setFocus();
                this.input_focused = true;
            }, 100);
        }
    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|order_email_modal");
    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading order").then(() => {
                this.orderService.getOrder(this.order_id).then((order) => {
                    if(order) {
                        this.order = order;
                    }
                    this.loading_spinner.dismiss();
                    resolve();
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    close() {
        this.modalController.dismiss();
    }

    send() {

        let validated = true;
        let email_address_regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        
        if(this.email_address == null || this.email_address.trim() === "") {
            this.presentAlert("Missing email address", "Please enter an email address.");
            validated = false;
        }
        else if(!email_address_regex.test(String(this.email_address).toLowerCase())) {
            this.presentAlert("Invalid email address", "");
            validated = false;
        }

        // NOTE: ignore repeat presses while the invoice is being emailed, otherwise a double tap/Enter sends it twice
        if(validated && !this.sending) {
            this.sending = true;
            this.presentSpinner('Emailing invoice').then(() => {
                this.orderService.sendOrderInvoiceEmail(this.order.id, this.email_address).then(() => {
                    this.modalController.dismiss();
                })
                .catch(() => {
                    this.sending = false;
                })
                .finally(() => {
                    this.spinner.dismiss();
                });
            });
        }

    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

}
